import { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { Lock, KeyRound, AlertCircle, ArrowLeft, Eye, EyeOff } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') || '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!token) {
      toast.error('This reset link is missing its token. Request a new link.');
      return;
    }
    if (password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    if (password !== confirm) {
      toast.error("Passwords don't match");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/api/auth/reset-password`, {
        token,
        new_password: password,
      });
      toast.success('Password updated. Sign in with your new password.');
      navigate('/login', { replace: true });
    } catch (err) {
      const detail = err.response?.data?.detail || 'Could not reset password. The link may have expired.';
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#FDFCFB] to-[#F5F3F0] flex items-center justify-center p-4">
        <Card className="w-full max-w-md shadow-xl border-0">
          <CardHeader className="text-center">
            <div className="mx-auto w-16 h-16 bg-amber-100 rounded-xl flex items-center justify-center mb-4">
              <AlertCircle className="w-8 h-8 text-amber-700" />
            </div>
            <CardTitle className="text-2xl font-bold text-[#1A1A1A]">Invalid reset link</CardTitle>
            <CardDescription className="text-gray-600">
              This password reset link is missing or malformed. Request a fresh link to continue.
            </CardDescription>
          </CardHeader>
          <CardFooter className="flex flex-col gap-3">
            <Link to="/forgot-password" data-testid="reset-password-request-new-link" className="w-full">
              <Button className="w-full bg-[#70170F] hover:bg-[#5a1209] text-white">
                Request a new link
              </Button>
            </Link>
            <Link to="/login" className="text-sm text-[#70170F] hover:underline inline-flex items-center">
              <ArrowLeft className="w-4 h-4 mr-1" />
              Back to sign in
            </Link>
          </CardFooter>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FDFCFB] to-[#F5F3F0] flex items-center justify-center p-4">
      <Card className="w-full max-w-md shadow-xl border-0">
        <CardHeader className="text-center pb-2">
          <div className="mx-auto w-16 h-16 bg-[#70170F] rounded-xl flex items-center justify-center mb-4">
            <KeyRound className="w-8 h-8 text-white" />
          </div>
          <CardTitle className="text-2xl font-bold text-[#1A1A1A]">Set a new password</CardTitle>
          <CardDescription className="text-gray-600">
            Pick something at least 8 characters long. You'll be signed in once it's saved.
          </CardDescription>
        </CardHeader>
        <form onSubmit={handleSubmit}>
          <CardContent className="space-y-4 pt-6">
            <div>
              <Label htmlFor="password" className="flex items-center gap-2 mb-1.5">
                <Lock className="w-4 h-4" /> New password
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  required
                  minLength={8}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pr-10"
                  data-testid="reset-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  data-testid="reset-password-toggle-visibility"
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-2 min-w-[32px] min-h-[32px] flex items-center justify-center text-gray-500 hover:text-[#70170F] focus:outline-none focus:ring-2 focus:ring-[#70170F] rounded"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div>
              <Label htmlFor="confirm" className="flex items-center gap-2 mb-1.5">
                <Lock className="w-4 h-4" /> Confirm password
              </Label>
              <div className="relative">
                <Input
                  id="confirm"
                  name="confirm"
                  type={showConfirm ? 'text' : 'password'}
                  autoComplete="new-password"
                  required
                  minLength={8}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className="pr-10"
                  data-testid="reset-password-confirm-input"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirm((v) => !v)}
                  aria-label={showConfirm ? 'Hide password' : 'Show password'}
                  data-testid="reset-password-confirm-toggle-visibility"
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-2 min-w-[32px] min-h-[32px] flex items-center justify-center text-gray-500 hover:text-[#70170F] focus:outline-none focus:ring-2 focus:ring-[#70170F] rounded"
                >
                  {showConfirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
          </CardContent>
          <CardFooter className="flex flex-col gap-3 pt-2">
            <Button
              type="submit"
              disabled={loading}
              data-testid="reset-password-submit-btn"
              className="w-full bg-[#70170F] hover:bg-[#5a1209] text-white"
            >
              {loading ? 'Updating…' : 'Update password'}
            </Button>
            <Link
              to="/login"
              className="inline-flex items-center text-sm text-[#70170F] hover:underline"
              data-testid="reset-password-back-link"
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              Cancel and go back to sign in
            </Link>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
}
